/**
 * 3D Visualizer — Render Pipeline
 *
 * Turns the render settings (renderSettings.ts) into a picture. Used by the 3D
 * Designer's viewer only; the wizard's viewers keep drawing straight to the
 * screen as they always have.
 *
 * Each frame:
 *   1. the scene is drawn into a high-range (float) target, so nothing clips
 *      before the tone curve gets to shape it;
 *   2. ambient occlusion darkens corners and creases (GTAO);
 *   3. bloom gathers the glow of the brightest areas;
 *      (before any of that, each glazed wall gets a mirror picture: the room
 *      drawn again as its glass would reflect it — see drawMirrors)
 *   4. the seams between boards are drawn on their own, as a mask (see below);
 *   5. one finishing pass does the rest on its way to the screen: sharpen,
 *      exposure and tone curve, colour grade, the seams, the backdrop behind
 *      the room, vignette and film grain.
 *
 * Seams are laid on AFTER the tone curve, not lit with the scene. A tone curve
 * squeezes the brightest values together — that is its job — and a seam drawn
 * in the scene is squeezed with them: the brighter the wall, the fainter its
 * joints, until a strongly lit wall reads as one seamless sheet. Drawn on the
 * finished picture they are the same strength in any light.
 *
 * Between frames, `apply()` pushes the settings into the scene's lights,
 * shadows and materials. The viewer builds the scene; the pipeline is handed
 * the pieces it adjusts (`attach`) every time the scene is rebuilt.
 *
 * Light units: a setting of 1.0 means "a white board is lit to full white where
 * this light hits it square on". three's lights are physical (a Lambert surface
 * returns irradiance / π), hence the factors of π below.
 */

import * as THREE from 'three';
import { Normal, lampCount, lampPlacements } from './lampLayout';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { meterCorrection } from './exposureMeter';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { ENV_IMAGES, loadEnvImage } from './sceneBuilder';
import { WOOD_TILE, WoodKind, configureWood, getWoodTexture } from './woodTexture';
import { loadStoredTextureBlob } from './textureLibrary';
import { parseSetTexture, textureSetPicture, textureSetSize } from './textureSets';
import {
  EnvironmentId,
  FLOOR_TEXTURES,
  RenderSettings,
  ShadowTypeId,
  ToneMappingId,
  getRenderSettings,
  hexToRgb,
  kelvinToRgb,
} from './renderSettings';

/** The parts of a freshly built scene the pipeline adjusts. */
export interface RenderRig {
  scene: THREE.Scene;
  /** Everything of the room, in room coordinates (origin at the front-left floor corner). */
  roomGroup: THREE.Group;
  floor: THREE.Mesh | null;
  hemi: THREE.HemisphereLight;
  key: THREE.DirectionalLight;
  fill: THREE.DirectionalLight;
  /** The lamp inside the room; absent in ceiling-only views. */
  interior: THREE.PointLight | null;
  dims: { length: number; width: number; height: number; ceilingAt?: (x: number, z: number) => number; ceilingNormalAt?: (x: number, z: number) => Normal };
  /** Floor plans: the other rooms' floors (each its own size), to be textured like the room's. */
  otherFloors?: { mesh: THREE.Mesh; length: number; width: number; key: string }[];
  /**
   * Floor plans: every room's footprint in room coordinates (this room's is
   * 0,0 → length,width), for the shadow on the ground. Absent = this room alone.
   */
  footprints?: { x: number; z: number; length: number; width: number }[];
  /**
   * Floor plans: where this room's front-left corner is on the plan's sheet
   * (feet), so the blueprint ground's grid lines up with the planner's.
   */
  sheetOrigin?: { x: number; z: number };
  /**
   * Floor plans: the other rooms, each with its own lamps (lit whichever room is
   * shown, so walking from one to the next changes nothing): its group, placed
   * in this room's coordinates, and its size.
   */
  otherRooms?: { key: string; group: THREE.Object3D; length: number; width: number; height: number; ceilingAt?: (x: number, z: number) => number; ceilingNormalAt?: (x: number, z: number) => Normal }[];
}

/** A room's lamp. */
// The exposure meter's reading: the lit scene at this size, each pixel's
// scene-linear luminance (0 … METER_RANGE, root-encoded into a byte so the
// shadows keep their precision) and its coverage, read back on the CPU.
const METER_W = 64;
const METER_H = 36;
const METER_RANGE = 16;
/** How far a new reading moves the exposure toward itself (1 = at once): a little lag, no flicker as the camera moves. */
const METER_SMOOTHING = 0.35;
/**
 * The meter's reading of one pixel: red its scene-linear luminance (square
 * rooted over METER_RANGE, so the byte keeps its shadows), green its coverage —
 * the lit picture's alpha where the ROOM is (tMask, drawn without the drafting
 * sheet and its shadow), nothing where only the sheet is. From outside the
 * room the sheet fills most of the picture; metered as room, its dark blue
 * pushed the exposure to the clamp and the room went white.
 */
const METER_FRAGMENT = `
    uniform sampler2D tDiffuse;
    uniform sampler2D tMask;
    varying vec2 vUv;
    void main() {
      vec4 t = texture2D( tDiffuse, vUv );
      float a = clamp( t.a, 0.0, 1.0 );
      vec3 c = a > 0.0001 ? max( t.rgb / a, vec3( 0.0 ) ) : vec3( 0.0 );
      float L = dot( c, vec3( 0.2126, 0.7152, 0.0722 ) );
      float room = texture2D( tMask, vUv ).r > 0.5 ? a : 0.0;
      gl_FragColor = vec4( sqrt( clamp( L / ${METER_RANGE.toFixed(1)}, 0.0, 1.0 ) ), room, 0.0, 1.0 );
    }`;

type RoomLamp = THREE.PointLight;

/** A room that gets lamps: the one shown (whose first lamp is the viewer's) or another of the plan. */
interface LampRoom {
  key: string;
  parent: THREE.Object3D;
  length: number;
  width: number;
  height: number;
  /** The ceiling's height over a point of the floor, when it is not flat. */
  ceilingAt?: (x: number, z: number) => number;
  /** The ceiling's normal (into the room) over a point of the floor, when it is not flat. */
  ceilingNormalAt?: (x: number, z: number) => Normal;
  first: THREE.PointLight | null;
  shown: boolean;
}

/** One room's lamps and the fixture drawn at each. */
interface LampSet {
  lamps: RoomLamp[];
  /** The lamps this pipeline made (the shown room's first is the viewer's). */
  created: RoomLamp[];
  fixtures: THREE.Group[];
  style: string;
}

/**
 * The blueprint ground: the planner's sheet under the rooms. Its colour at the
 * chosen opacity, with a fine grid every foot and a bold one every few feet,
 * drawn a constant width on screen (screen-space derivatives); fading away past
 * the rooms. Output is premultiplied by its coverage, like the rest of the
 * scene, so the backdrop shows where it is see-through.
 */
const BLUEPRINT_VERTEX = `
  varying vec2 vPos;
  void main() {
    vec4 world = modelMatrix * vec4( position, 1.0 );
    vPos = world.xz;
    gl_Position = projectionMatrix * viewMatrix * world;
  }`;
const BLUEPRINT_FRAGMENT = `
  uniform vec3 uSheet;
  uniform float uSheetOpacity;
  uniform vec3 uLine;
  uniform float uLineOpacity;
  uniform float uLineWidth;
  uniform float uMajorEvery;
  uniform float uMajorWeight;
  uniform vec2 uOffset;
  uniform vec2 uCentre;
  uniform float uFadeFrom;
  uniform float uFadeTo;
  varying vec2 vPos;
  float gridLine( vec2 p, float spacing, float widthPx ) {
    vec2 d = abs( fract( p / spacing - 0.5 ) - 0.5 ) * spacing; // distance to the nearest line
    vec2 w = max( fwidth( p ) * widthPx, vec2( 1e-5 ) );
    vec2 l = 1.0 - smoothstep( w * 0.5, w * 0.5 + fwidth( p ), d );
    return max( l.x, l.y );
  }
  void main() {
    vec2 p = vPos + uOffset;
    float line = gridLine( p, 1.0, uLineWidth ) * 0.6;
    if ( uMajorEvery > 0.5 ) line = max( line, gridLine( p, uMajorEvery, uLineWidth * uMajorWeight ) );
    line *= uLineOpacity;
    float fade = 1.0 - smoothstep( uFadeFrom, uFadeTo, length( vPos - uCentre ) );
    float alpha = max( uSheetOpacity, line );
    vec3 colour = ( uSheet * uSheetOpacity * ( 1.0 - line ) + uLine * line ) / max( alpha, 1e-4 );
    alpha *= fade;
    gl_FragColor = vec4( colour * alpha, alpha );
  }`;

const ENV_PATH: Partial<Record<EnvironmentId, string>> = {
  garage1: ENV_IMAGES[0].path,
  garage2: ENV_IMAGES[1].path,
  garage3: ENV_IMAGES[2].path,
};

const SHADOW_TYPE: Record<ShadowTypeId, THREE.ShadowMapType> = {
  basic: THREE.BasicShadowMap,
  pcf: THREE.PCFShadowMap,
  pcfsoft: THREE.PCFSoftShadowMap,
  vsm: THREE.VSMShadowMap,
};

const TONE_CURVE: Record<ToneMappingId, number> = {
  accurate: 0,
  linear: 1,
  neutral: 2,
  reinhard: 3,
  cineon: 4,
  aces: 5,
  agx: 6,
};

/**
 * Budgets that keep the frame within what a modest GPU holds. The scene is
 * drawn into a float target, which costs 8 bytes a pixel — and that again for
 * every anti-aliasing sample.
 */
const MAX_AUTO_PIXELS = 16e6;      // "Auto" render scale never asks for a bigger frame than this
const MAX_SAMPLE_PIXELS = 48e6;    // pixels × anti-aliasing samples

/** The board materials' own emissive intensity (materialFactory): the "self-glow" setting is relative to it. */
const BASE_GLOW = 0.4;

type WallSide = 'front' | 'back' | 'left' | 'right';

// Scratch values for the mirror pass (one frame at a time; nothing is kept in them).
const PLANE_NORMAL = new THREE.Vector3();
const PLANE_POINT = new THREE.Vector3();
const CAMERA_POSITION = new THREE.Vector3();
const VIEW = new THREE.Vector3();
const LOOK_AT = new THREE.Vector3();
const TARGET = new THREE.Vector3();
const ROTATION = new THREE.Matrix4();
const MIRROR_MATRIX = new THREE.Matrix4();
const MIRROR_PLANE = new THREE.Plane();
const CLIP = new THREE.Vector4();
const CORNER = new THREE.Vector4();
const MIRROR_MATRICES = new Map<WallSide, THREE.Matrix4>();

/** The colour the wooden door styles are built in (openingStyles.ts); a chosen wood colour replaces it, part for part. */
const DOOR_WOOD_REFERENCE = 0x7a5540;

/** First spherical-harmonic coefficient of a light probe that adds exactly `intensity` of even white light. */
const FLAT_PROBE_SH = Math.PI / 0.886227;

const FINISH_SHADER = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    tBloom: { value: null as THREE.Texture | null },
    uBloom: { value: 0 },
    tSeams: { value: null as THREE.Texture | null },
    uSeams: { value: 0 },
    uTexel: { value: new THREE.Vector2(1, 1) },
    uCurve: { value: 0 },
    toneMappingExposure: { value: 1 },
    uRolloff: { value: 0.15 },
    uBrightness: { value: 0 },
    uContrast: { value: 1 },
    uSaturation: { value: 1 },
    uTemperature: { value: 0 },
    uVignette: { value: 0 },
    uVignetteSoftness: { value: 0.6 },
    uGrain: { value: 0 },
    uSharpen: { value: 0 },
    uSoftness: { value: 0 },
    uBackdropTop: { value: new THREE.Vector3(1, 1, 1) },
    uBackdropBottom: { value: new THREE.Vector3(1, 1, 1) },
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }`,
  fragmentShader: /* glsl */`
    uniform sampler2D tDiffuse;
    uniform sampler2D tBloom;
    uniform float uBloom;
    uniform sampler2D tSeams;
    uniform float uSeams;
    uniform vec2 uTexel;
    uniform int uCurve;
    uniform float uRolloff;
    uniform float uBrightness;
    uniform float uContrast;
    uniform float uSaturation;
    uniform float uTemperature;
    uniform float uVignette;
    uniform float uVignetteSoftness;
    uniform float uGrain;
    uniform float uSharpen;
    uniform float uSoftness;
    uniform vec3 uBackdropTop;
    uniform vec3 uBackdropBottom;
    varying vec2 vUv;

    #include <tonemapping_pars_fragment>

    // Product colours exactly as lit, up to a knee just under white; above it the
    // brightest values ease toward white instead of clipping (so a hot spot keeps
    // its shading). The curve is continuous in value and slope at the knee.
    vec3 AccurateToneMapping( vec3 color ) {
      color *= toneMappingExposure;
      float peak = max( color.r, max( color.g, color.b ) );
      float knee = 1.0 - uRolloff;
      if ( uRolloff < 0.001 || peak <= knee ) return saturate( color );
      float newPeak = 1.0 - uRolloff * uRolloff / ( peak + uRolloff - knee );
      return color * ( newPeak / peak );
    }

    // Khronos PBR Neutral (three r162+), for product colour under a filmic shoulder.
    vec3 NeutralToneMapping( vec3 color ) {
      const float StartCompression = 0.8 - 0.04;
      const float Desaturation = 0.15;
      color *= toneMappingExposure;
      float x = min( color.r, min( color.g, color.b ) );
      float offset = x < 0.08 ? x - 6.25 * x * x : 0.04;
      color -= offset;
      float peak = max( color.r, max( color.g, color.b ) );
      if ( peak < StartCompression ) return color;
      float d = 1. - StartCompression;
      float newPeak = 1. - d * d / ( peak + d - StartCompression );
      color *= newPeak / peak;
      float g = 1. - 1. / ( Desaturation * ( peak - newPeak ) + 1. );
      return mix( color, vec3( newPeak ), g );
    }

    vec3 toneCurve( vec3 color ) {
      if ( uCurve == 0 ) return AccurateToneMapping( color );
      if ( uCurve == 1 ) return LinearToneMapping( color );
      if ( uCurve == 2 ) return saturate( NeutralToneMapping( color ) );
      if ( uCurve == 3 ) return ReinhardToneMapping( color );
      if ( uCurve == 4 ) return OptimizedCineonToneMapping( color );
      if ( uCurve == 5 ) return ACESFilmicToneMapping( color );
      return AgXToneMapping( color );
    }

    vec3 toDisplay( vec3 c ) {
      return mix( pow( c, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), c * 12.92, vec3( lessThanEqual( c, vec3( 0.0031308 ) ) ) );
    }

    float hash( vec2 p ) {
      vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
      p3 += dot( p3, p3.yzx + 33.33 );
      return fract( ( p3.x + p3.y ) * p3.z );
    }

    void main() {
      // The scene, premultiplied by its coverage: alpha is 0 where there is only backdrop.
      vec4 texel = texture2D( tDiffuse, vUv );

      // A lens's softness first (a box of neighbours, wider as it grows, mixed in),
      // then any sharpening — the order a photograph is made in.
      if ( uSoftness > 0.0 ) {
        vec2 step = uTexel * ( 1.0 + 2.0 * uSoftness );
        vec4 soft = ( texel
          + texture2D( tDiffuse, vUv + vec2( step.x, 0.0 ) ) + texture2D( tDiffuse, vUv - vec2( step.x, 0.0 ) )
          + texture2D( tDiffuse, vUv + vec2( 0.0, step.y ) ) + texture2D( tDiffuse, vUv - vec2( 0.0, step.y ) )
          + texture2D( tDiffuse, vUv + step ) + texture2D( tDiffuse, vUv - step )
          + texture2D( tDiffuse, vUv + vec2( step.x, -step.y ) ) + texture2D( tDiffuse, vUv - vec2( step.x, -step.y ) ) ) / 9.0;
        texel = mix( texel, soft, min( uSoftness, 1.0 ) );
      }
      if ( uSharpen > 0.0 ) {
        vec3 around = texture2D( tDiffuse, vUv + vec2( uTexel.x, 0.0 ) ).rgb
          + texture2D( tDiffuse, vUv - vec2( uTexel.x, 0.0 ) ).rgb
          + texture2D( tDiffuse, vUv + vec2( 0.0, uTexel.y ) ).rgb
          + texture2D( tDiffuse, vUv - vec2( 0.0, uTexel.y ) ).rgb;
        texel.rgb = max( texel.rgb + ( texel.rgb - around * 0.25 ) * uSharpen, vec3( 0.0 ) );
      }

      float alpha = clamp( texel.a, 0.0, 1.0 );
      vec3 scene = alpha > 0.0001 ? max( texel.rgb / alpha, vec3( 0.0 ) ) : vec3( 0.0 );
      vec3 bloom = uBloom > 0.0 ? max( texture2D( tBloom, vUv ).rgb, vec3( 0.0 ) ) * uBloom : vec3( 0.0 );

      // Light to screen: tone curve, then display encoding.
      vec3 color = toDisplay( saturate( toneCurve( scene + bloom ) ) );

      // Grade (on display values, like a photo editor).
      color.r *= 1.0 + uTemperature * 0.08;
      color.b *= 1.0 - uTemperature * 0.08;
      color = ( color - 0.5 ) * uContrast + 0.5 + uBrightness;
      color = mix( vec3( dot( color, vec3( 0.2126, 0.7152, 0.0722 ) ) ), color, uSaturation );

      // Seams over the finished boards (their colour premultiplied by their coverage).
      if ( uSeams > 0.0 ) {
        vec4 seam = texture2D( tSeams, vUv );
        color = color * ( 1.0 - seam.a ) + seam.rgb;
      }

      // Backdrop behind the room: the chosen colours as picked (exposure does not
      // touch them), with any bloom glowing over it.
      vec3 backdrop = mix( uBackdropBottom, uBackdropTop, smoothstep( 0.0, 1.0, vUv.y ) );
      vec3 glow = toDisplay( saturate( toneCurve( bloom ) ) );
      backdrop = 1.0 - ( 1.0 - backdrop ) * ( 1.0 - glow );
      color = mix( backdrop, color, alpha );

      // Lens.
      float d = length( ( vUv - 0.5 ) * 2.0 );
      float shade = 1.0 - smoothstep( 1.2 - uVignetteSoftness, 1.45, d );
      color *= mix( 1.0, shade, uVignette );
      color += ( hash( gl_FragCoord.xy ) - 0.5 ) * uGrain;

      gl_FragColor = vec4( saturate( color ), 1.0 );
    }`,
};

// ============================================================================
// Room lamps — where they go and what is drawn at each
// ============================================================================

/**
 * The fixture drawn at a lamp: bright enough to read as lit (and to bloom); it
 * casts no shadow and gives no light of its own — the lamp inside does that.
 * Built with its "down" along -Y, so it can be turned to lie on a sloped ceiling.
 *   dome — a glowing half-globe on a round base, facing down
 *   pot  — a glowing disc in a trim ring, recessed in the ceiling
 *   bar  — a slim shop-light bar, lying along the room
 */
function buildFixture(style: string): THREE.Group {
  const group = new THREE.Group();
  group.name = 'light-fixture';
  const glow = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const trim = new THREE.MeshStandardMaterial({ color: 0xf4f4f2, roughness: 0.45 });
  group.userData.glow = glow;
  if (style === 'dome') {
    group.add(new THREE.Mesh(new THREE.SphereGeometry(0.5, 28, 10, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), glow));
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.58, 0.05, 36), trim);
    base.position.y = 0.025;
    group.add(base);
  } else if (style === 'pot') {
    const disc = new THREE.Mesh(new THREE.CircleGeometry(0.25, 32), glow);
    disc.rotation.x = Math.PI / 2; // facing down
    group.add(disc);
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.25, 0.34, 36), trim);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = -0.004;
    group.add(ring);
  } else {
    group.add(new THREE.Mesh(new THREE.BoxGeometry(4, 0.14, 0.42), glow));
  }
  group.traverse((c) => {
    c.userData.skipAO = true;
    c.castShadow = false;
    c.receiveShadow = false;
  });
  return group;
}

const FIXTURE_DOWN = new THREE.Vector3(0, -1, 0);

/**
 * Put a fixture at its lamp: on the ceiling, lying flat on it whichever way it
 * slopes (its "down" turned to the ceiling's normal), set just under the
 * boards; a bar hangs level below its lamp.
 */
/** Put a lamp's fixture where the lamp hangs (`lampAt`, the room's own coordinates). */
function placeFixture(fixture: THREE.Group | undefined, style: string, lamp: RoomLamp, lampAt: THREE.Vector3, alongX: boolean, mount: THREE.Vector3, normal: THREE.Vector3): void {
  if (!fixture) return;
  if (style === 'bar') {
    fixture.position.copy(lampAt);
    fixture.position.y = Math.min(mount.y - 0.12, lampAt.y + 0.2);
    fixture.rotation.set(0, alongX ? 0 : Math.PI / 2, 0);
  } else {
    const inset = style === 'dome' ? 0.08 : 0.055;
    fixture.position.copy(mount).addScaledVector(normal, inset);
    fixture.quaternion.setFromUnitVectors(FIXTURE_DOWN, normal);
  }
  // The lamp's colour, well over white: it is a light, not a white shape.
  (fixture.userData.glow as THREE.MeshBasicMaterial).color.copy(lamp.color).multiplyScalar(style === 'dome' ? 2.4 : 3.2);
}

function disposeLamp(lamp: RoomLamp): void {
  lamp.removeFromParent();
  lamp.dispose();
}

function disposeFixture(fixture: THREE.Group): void {
  fixture.removeFromParent();
  fixture.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
  });
}

type EnvEntry = { envMap: THREE.Texture | null; sh: THREE.Vector3[] | null };

export class RenderPipeline {
  private readonly renderer: THREE.WebGLRenderer;
  /** Called when something finished loading and the view should be drawn again. */
  private readonly invalidate: () => void;
  private rig: RenderRig | null = null;

  private sceneTarget: THREE.WebGLRenderTarget | null = null;
  private aoTarget: THREE.WebGLRenderTarget | null = null;
  private targetKey = '';
  private aoKey = '';

  private gtao: GTAOPass | null = null;
  private bloom: UnrealBloomPass | null = null;

  /**
   * The exposure meter (exposureMeter.ts): the lit scene read back at 64 × 36,
   * its luminance in scene-linear light, and the stops that bring the metered
   * percentile to the target — smoothed a little between readings so the
   * picture does not flicker as the camera moves.
   */
  private readonly meterMaterial: THREE.ShaderMaterial;
  private readonly meterQuad: FullScreenQuad;
  private readonly meterTarget: THREE.WebGLRenderTarget;
  private readonly meterBuf = new Uint8Array(METER_W * METER_H * 4);
  /** Where the room is in the picture (METER_FRAGMENT tMask): the scene drawn flat at the meter's size, the sheet and its shadow left out. */
  private readonly maskMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide });
  private readonly maskTarget: THREE.WebGLRenderTarget;
  private autoEv = 0;
  private autoEvKnown = false;
  private frame = 0;

  /** The seam meshes of the current scene; the pipeline draws them itself, into `seamTarget`. */
  private seams: THREE.Mesh[] = [];
  private seamTarget: THREE.WebGLRenderTarget | null = null;
  /** Writes depth and nothing else: lays down what can hide a seam. */
  private readonly depthOnly = new THREE.MeshBasicMaterial({ colorWrite: false, side: THREE.DoubleSide });
  private readonly visibility = new Map<THREE.Object3D, boolean>();
  private readonly finish: THREE.ShaderMaterial;
  private readonly quad: FullScreenQuad;

  private readonly probe = new THREE.LightProbe();
  private readonly environments = new Map<EnvironmentId, EnvEntry>();
  private readonly loadingEnvironments = new Set<EnvironmentId>();

  private ground: THREE.Mesh | null = null;
  private groundTexture: THREE.Texture | null = null;
  private blueprint: THREE.Mesh | null = null;

  /** The shadow maps need drawing again before the next frame (see render). */
  private shadowsDirty = true;

  /**
   * Something a shadow depends on has changed — a mesh that casts one moved,
   * appeared or went, a lamp moved — so the maps are drawn again next frame.
   * The camera moving is NOT such a change: shadows do not depend on it.
   */
  invalidateShadows(): void {
    this.shadowsDirty = true;
  }

  /** Lamps the pipeline adds beside the viewer's own, when the settings ask for more than one. */
  /** Every room's lamps, and a glowing fixture at each (so the lights are something you can see, and see reflected). */
  private lampSets = new Map<string, LampSet>();

  /** The glass of each wall, with the picture of the room it mirrors. */
  private glazing: { face: WallSide; wall: THREE.Object3D; glass: THREE.Mesh }[] = [];
  private readonly mirrors = new Map<WallSide, THREE.WebGLRenderTarget>();
  private readonly mirrorCameras = { perspective: new THREE.PerspectiveCamera() };
  private readonly forced: { material: THREE.Material; opacity: number; depthWrite: boolean }[] = [];
  private readonly shown: { object: THREE.Object3D; visible: boolean }[] = [];
  private settings: RenderSettings = getRenderSettings();
  private disposed = false;

  private readonly size = new THREE.Vector2();
  private readonly clearColor = new THREE.Color();
  private readonly viewDir = new THREE.Vector3();

  /** Pictures used as materials (built-in floors, uploads), by id: the loaded image, or why not. */
  private readonly pictures = new Map<string, THREE.Texture | 'loading' | 'missing'>();
  /** One texture per (use, picture): the same image, scaled and turned for that use. */
  private readonly pictureUses = new Map<string, THREE.Texture>();
  private readonly tint = new THREE.Color();
  private readonly hsl = { h: 0, s: 0, l: 0 };

  constructor(renderer: THREE.WebGLRenderer, invalidate: () => void) {
    this.renderer = renderer;
    this.invalidate = invalidate;
    this.finish = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(FINISH_SHADER.uniforms),
      vertexShader: FINISH_SHADER.vertexShader,
      fragmentShader: FINISH_SHADER.fragmentShader,
      depthTest: false,
      depthWrite: false,
      // The tone curve is this shader's own work; keep the renderer's out of it.
      toneMapped: false,
    });
    this.quad = new FullScreenQuad(this.finish);
    this.maskTarget = new THREE.WebGLRenderTarget(METER_W, METER_H, { depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    this.maskTarget.texture.name = 'designer.meter-mask';
    this.meterMaterial = new THREE.ShaderMaterial({
      uniforms: { tDiffuse: { value: null }, tMask: { value: this.maskTarget.texture } },
      vertexShader: FINISH_SHADER.vertexShader,
      fragmentShader: METER_FRAGMENT,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    this.meterQuad = new FullScreenQuad(this.meterMaterial);
    this.meterTarget = new THREE.WebGLRenderTarget(METER_W, METER_H, { depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    this.meterTarget.texture.name = 'designer.meter';
    // Before the first scene is built, so its wood is drawn to the saved recipe from the start.
    this.applyWoodRecipes(this.settings);
  }

  /** The exposure the meter has settled on, in stops (0 until it has read a picture). */
  get meteredEv(): number {
    return this.autoEv;
  }

  /**
   * Read the lit scene (scene-linear, before the tone curve) and move the
   * metered exposure toward what brings its 70th percentile to the target.
   * The reading does not depend on the exposure in force, so there is no
   * feedback loop: each reading is the answer, smoothed against the last.
   *
   * Only the ROOM is read: the scene is first drawn flat at the meter's size
   * with the drafting sheet and its shadow hidden (the mask), and a pixel
   * counts where that shows something. Inside the room the mask changes
   * nothing (every pixel is room, as the render's are); from outside, the
   * sheet round the room — dark blue, most of the picture from above — no
   * longer drags the 70th percentile down and the exposure up to the clamp.
   */
  private meter(scene: THREE.Scene, camera: THREE.Camera, lit: THREE.WebGLRenderTarget): void {
    const renderer = this.renderer;
    const sheet = this.blueprint, shadow = this.ground;
    const shown = [sheet?.visible, shadow?.visible];
    if (sheet) sheet.visible = false;
    if (shadow) shadow.visible = false;
    const background = scene.background;
    scene.background = null;
    scene.overrideMaterial = this.maskMaterial;
    renderer.setRenderTarget(this.maskTarget);
    renderer.render(scene, camera); // clears to (0, 0, 0, 0): the room white, nothing else
    scene.overrideMaterial = null;
    scene.background = background;
    if (sheet) sheet.visible = shown[0]!;
    if (shadow) shadow.visible = shown[1]!;

    this.meterMaterial.uniforms.tDiffuse.value = lit.texture;
    renderer.setRenderTarget(this.meterTarget);
    this.meterQuad.render(renderer);
    renderer.readRenderTargetPixels(this.meterTarget, 0, 0, METER_W, METER_H, this.meterBuf);
    renderer.setRenderTarget(null);
    const lums: number[] = [];
    const buf = this.meterBuf;
    for (let i = 0; i < buf.length; i += 4) {
      if (buf[i + 1] < 128) continue; // backdrop, not room
      const e = buf[i] / 255;
      lums.push(e * e * METER_RANGE);
    }
    const ev = meterCorrection(lums);
    // The last reading, for anyone checking the meter from the console (tests, a screenshot session): no logging.
    (window as unknown as { __TRUSSCORE_METER__?: unknown }).__TRUSSCORE_METER__ = { ev, pixels: lums.length, of: buf.length / 4, applied: this.autoEv };
    if (ev === null) return;
    const next = this.autoEvKnown ? this.autoEv + (ev - this.autoEv) * METER_SMOOTHING : ev;
    this.autoEvKnown = true;
    if (Math.abs(next - this.autoEv) > 0.004) {
      this.autoEv = next;
      this.invalidate();
    }
  }

  // ==========================================================================
  // Scene hookup
  // ==========================================================================

  /** Take over a freshly built scene. Call after every rebuild, then `apply()` is run for you. */
  attach(rig: RenderRig): void {
    this.rig = rig;
    this.shadowsDirty = true; // a new scene: everything casts afresh
    rig.scene.add(this.probe);
    // The last scene's lamps (in its scene) and fixtures (in its rooms) went
    // with it: take them out and free their shadow maps and meshes.
    this.lampSets.forEach((set) => { set.created.forEach(disposeLamp); set.fixtures.forEach(disposeFixture); });
    this.lampSets.clear();
    this.otherRoomsLit = 1;
    this.buildGround(rig);
    // True-width seams replace the ceiling's hairline ones (see seamStrips.ts),
    // and are drawn by the pipeline in a pass of their own — never with the scene.
    this.seams = [];
    this.glazing = [];
    rig.roomGroup.traverse((child) => {
      if (child.userData.legacySeams) child.visible = false;
      if (!(child instanceof THREE.Mesh)) return;
      const kind = (child.material as THREE.Material).userData?.kind;
      if (kind === 'seam') {
        child.visible = false;
        this.seams.push(child);
      } else if (kind === 'glass') {
        // Which wall it is set in: the group the viewer tagged with its face.
        let wall: THREE.Object3D | null = child.parent;
        while (wall && !wall.userData.wallFace) wall = wall.parent;
        const face = wall?.userData.wallFace as string | undefined;
        if (wall && (face === 'front' || face === 'back' || face === 'left' || face === 'right')) {
          this.glazing.push({ face, wall, glass: child });
        }
      }
    });
    this.apply();
  }

  // ==========================================================================
  // Settings → scene
  // ==========================================================================

  apply(settings: RenderSettings = getRenderSettings()): void {
    if (this.disposed) return;
    this.settings = settings;
    this.shadowsDirty = true; // lamps may have moved, come or gone
    const s = settings;

    // Auto: 2× the screen's own pixels at least (as the viewer always has), eased
    // off on a very large canvas.
    const device = window.devicePixelRatio || 1;
    this.renderer.getSize(this.size);
    const affordable = Math.sqrt(MAX_AUTO_PIXELS / Math.max(1, this.size.x * this.size.y));
    const pixelRatio = s.renderScale > 0 ? s.renderScale : Math.max(device, Math.min(2, affordable));
    if (Math.abs(this.renderer.getPixelRatio() - pixelRatio) > 1e-3) this.renderer.setPixelRatio(pixelRatio);

    this.applyFinish(s);
    this.applyWoodRecipes(s);
    if (this.bloom) {
      this.bloom.strength = 1; // strength is applied in the finishing pass
      this.bloom.radius = s.bloomRadius;
      this.bloom.threshold = s.bloomThreshold;
      // How far above the threshold an area must be to glow in full. The pass's
      // own value is a cliff (0.01): a white wall a hair over the threshold
      // glowed as hard as a lamp, and the glow fogged everything near it — a
      // gray SlatWall band on that wall washed out to white. Eased in, the glow
      // grows with how much brighter than the threshold the area really is.
      (this.bloom.highPassUniforms as { smoothWidth: { value: number } }).smoothWidth.value = s.bloomKnee;
    }
    if (this.gtao) this.applyOcclusion(s);

    if (!this.rig) return;
    this.applyLights(s, this.rig);
    this.applyShadows(s, this.rig);
    this.applyEnvironment(s, this.rig);
    this.applyMaterials(s, this.rig);
    if (this.ground) {
      this.ground.visible = s.groundShadow > 0.005;
      (this.ground.material as THREE.MeshBasicMaterial).opacity = s.groundShadow;
    }
    if (this.blueprint) {
      const u = (this.blueprint.material as THREE.ShaderMaterial).uniforms;
      this.blueprint.visible = s.blueprint;
      (u.uSheet.value as THREE.Color).set(s.blueprintColor);
      u.uSheetOpacity.value = s.blueprintOpacity;
      (u.uLine.value as THREE.Color).set(s.blueprintLineColor);
      u.uLineOpacity.value = s.blueprintLineOpacity;
      u.uLineWidth.value = s.blueprintLineWidth;
      u.uMajorEvery.value = s.blueprintMajorEvery;
      u.uMajorWeight.value = s.blueprintMajorWeight;
      u.uFadeTo.value = (u.uFadeFrom.value as number) + Math.max(0.5, s.blueprintReach);
    }
  }

  private applyFinish(s: RenderSettings): void {
    const u = this.finish.uniforms;
    u.uCurve.value = TONE_CURVE[s.toneMapping];
    u.toneMappingExposure.value = s.exposure;
    u.uRolloff.value = s.highlightRolloff;
    u.uBrightness.value = s.brightness;
    u.uContrast.value = s.contrast;
    u.uSaturation.value = s.saturation;
    u.uTemperature.value = s.temperature;
    u.uVignette.value = s.vignette;
    u.uVignetteSoftness.value = s.vignetteSoftness;
    u.uGrain.value = s.grain;
    u.uSharpen.value = s.sharpen;
    u.uSoftness.value = s.softness;
    if (!s.autoExposure) { this.autoEv = 0; this.autoEvKnown = false; }
    (u.uBackdropTop.value as THREE.Vector3).fromArray(hexToRgb(s.backdropTop));
    (u.uBackdropBottom.value as THREE.Vector3).fromArray(hexToRgb(s.backdropBottom));
  }

  private applyOcclusion(s: RenderSettings): void {
    if (!this.gtao) return;
    this.gtao.blendIntensity = s.aoIntensity;
    this.gtao.updateGtaoMaterial({
      radius: s.aoRadius,
      distanceFallOff: s.aoFalloff,
      thickness: s.aoThickness,
      samples: s.aoSamples,
      distanceExponent: 1,
      scale: 1,
      screenSpaceRadius: false,
    });
  }

  private applyLights(s: RenderSettings, rig: RenderRig): void {

    rig.hemi.intensity = s.ambientIntensity * Math.PI;
    rig.hemi.color.set(s.ambientSky);
    rig.hemi.groundColor.set(s.ambientGround);

    rig.key.intensity = s.keyIntensity * Math.PI;
    rig.key.color.setRGB(...kelvinToRgb(s.keyWarmth), THREE.SRGBColorSpace);
    rig.fill.intensity = s.fillIntensity * Math.PI;
    rig.fill.color.copy(rig.key.color);
    if (s.keyMode === 'fixed') this.aimKeyLights(s.keyAzimuth, s.keyElevation, s.keyAzimuth + 180, Math.max(10, s.keyElevation * 0.6));

    for (const room of this.lampRooms(rig)) {
      const set = this.lampSet(s, room, rig.scene);
      const { length: L, width: W, height: H } = room;
      const placements = lampPlacements(s, L, W, H, room.ceilingAt, room.ceilingNormalAt);
      const alongX = L >= W;
      // Scaled to the room, so "0.5" lights the nearest wall the same in a shed and in a shop.
      const toNearestWall = s.interiorLayout === 'grid'
        ? Math.max(1, Math.min(L, W) / 3)
        : s.interiorLayout === 'array'
          ? Math.max(1, ...placements.map((p) => Math.min(p.mount.x, L - p.mount.x, p.mount.z, W - p.mount.z)))
          : Math.max(1, Math.min(alongX ? W * s.interiorZ : L * s.interiorX, alongX ? W * (1 - s.interiorZ) : L * (1 - s.interiorX)));
      const each = (s.interiorIntensity / set.lamps.length) * Math.PI * Math.pow(toNearestWall, s.interiorFalloff);
      // Which lamps get shadows when there are more than the GPU can take (see
      // applyShadows): the ones nearest the middle of the room first.
      const rank = new Map<number, number>();
      placements
        .map((p, i) => ({ i, d: Math.hypot(p.mount.x - L / 2, p.mount.z - W / 2) }))
        .sort((a, b) => a.d - b.d)
        .forEach(({ i }, r) => rank.set(i, r));
      // The lamps this pipeline made hang in the SCENE, not in their room's
      // group (see lampSet): their positions are the room's, moved by where
      // the room stands.
      room.parent.updateWorldMatrix(true, false);
      const standing = room.parent.getWorldPosition(new THREE.Vector3());
      set.lamps.forEach((lamp, i) => {
        const p = placements[i];
        lamp.userData.shadowRank = rank.get(i) ?? i;
        lamp.position.copy(p.lamp);
        if (lamp.parent === rig.scene) lamp.position.add(standing);
        lamp.color.setRGB(...kelvinToRgb(s.interiorWarmth), THREE.SRGBColorSpace);
        lamp.decay = s.interiorFalloff;
        lamp.distance = s.interiorReach > 0 ? s.interiorReach * Math.max(L, W) : 0;
        // Another room's lamps dim with it when it steps aside (setOtherRoomsLit).
        lamp.userData.lit = each;
        lamp.intensity = room.shown ? each : each * this.otherRoomsLit;
        placeFixture(set.fixtures[i], s.interiorFixtureStyle, lamp, p.lamp, alongX, p.mount, p.normal);
        // three shrinks a lamp's shadow range to its reach and never grows it
        // back: left alone, a lamp that once had a reach keeps a hard-edged
        // ball of light around it (everything beyond counts as in shadow).
        const shadowFar = lamp.distance || Math.max(L, W, H) * 3;
        if (lamp.shadow.camera.far !== shadowFar) {
          lamp.shadow.camera.far = shadowFar;
          lamp.shadow.camera.updateProjectionMatrix();
        }
      });
    }
  }

  /** The rooms that get lamps: the one shown, then the plan's others. */
  private lampRooms(rig: RenderRig): LampRoom[] {
    if (!rig.interior) return [];
    return [
      { key: ':shown', parent: rig.roomGroup, ...rig.dims, first: rig.interior, shown: true },
      ...(rig.otherRooms ?? []).map((r) => ({ key: r.key, parent: r.group, length: r.length, width: r.width, height: r.height, ceilingAt: r.ceilingAt, first: null, shown: false })),
    ];
  }

  /** One room's lamps (and fixtures) — made to match the layout and style the settings ask for. */
  private lampSet(s: RenderSettings, room: LampRoom, scene: THREE.Scene): LampSet {
    let set = this.lampSets.get(room.key);
    if (!set) {
      set = { lamps: [], created: [], fixtures: [], style: '' };
      this.lampSets.set(room.key, set);
    }
    const count = lampCount(s, room.length, room.width);
    const own = room.first ? 1 : 0;
    while (set.created.length > count - own) disposeLamp(set.created.pop()!);
    while (set.created.length < count - own) {
      const lamp: RoomLamp = new THREE.PointLight(0xffffff, 1);
      lamp.shadow.camera.near = room.first?.shadow.camera.near ?? 0.5;
      // In the scene, not the room's group: a room stepping aside (its group
      // hidden) would take its lamps out of the light count, and the number
      // of lights is compiled into every lit shader — every material would
      // be compiled again on every lock and unlock. The fixtures stay with
      // the room; the lamps just dim (setOtherRoomsLit).
      scene.add(lamp);
      set.created.push(lamp);
    }
    set.lamps = room.first ? [room.first, ...set.created] : [...set.created];
    if (set.style !== s.interiorFixtureStyle || set.fixtures.length !== count) {
      for (const f of set.fixtures) disposeFixture(f);
      set.fixtures = Array.from({ length: count }, () => {
        const f = buildFixture(s.interiorFixtureStyle);
        room.parent.add(f);
        return f;
      });
      set.style = s.interiorFixtureStyle;
    }
    return set;
  }

  /** Every lamp of every room, with whether it is the shown room's. */
  private allLamps(s: RenderSettings, rig: RenderRig): { lamp: RoomLamp; shown: boolean }[] {
    return this.lampRooms(rig).flatMap((room) => this.lampSet(s, room, rig.scene).lamps.map((lamp) => ({ lamp, shown: room.shown })));
  }

  /** How lit the other rooms are (1 = as the settings say, 0 = dark): they dim as they step aside. */
  private otherRoomsLit = 1;

  /**
   * Dim or restore the other rooms' lamps — their light, not their presence,
   * so the shaders' light count holds while a room steps out of the way.
   */
  setOtherRoomsLit(factor: number): void {
    const f = Math.max(0, Math.min(1, factor));
    if (f === this.otherRoomsLit) return;
    this.otherRoomsLit = f;
    this.lampSets.forEach((set, key) => {
      if (key === ':shown') return;
      for (const lamp of set.lamps) lamp.intensity = ((lamp.userData.lit as number | undefined) ?? lamp.intensity) * f;
    });
  }

  private allFixtures(): THREE.Group[] {
    return [...this.lampSets.values()].flatMap((set) => set.fixtures);
  }

  /** Point the key and fill lights along compass directions (degrees; 0° = from the front, +Z). */
  private aimKeyLights(keyAz: number, keyEl: number, fillAz: number, fillEl: number): void {
    if (!this.rig) return;
    const place = (light: THREE.DirectionalLight, azimuth: number, elevation: number) => {
      const az = THREE.MathUtils.degToRad(azimuth);
      const el = THREE.MathUtils.degToRad(Math.max(-89, Math.min(89, elevation)));
      const x = Math.sin(az) * Math.cos(el) * 30, y = Math.sin(el) * 30, z = Math.cos(az) * Math.cos(el) * 30;
      if (Math.abs(light.position.x - x) + Math.abs(light.position.y - y) + Math.abs(light.position.z - z) < 1e-6) return;
      light.position.set(x, y, z);
      // The key light casts a shadow: moved (following the camera), it must be drawn again.
      this.shadowsDirty = true;
    };
    place(this.rig.key, keyAz, keyEl);
    place(this.rig.fill, fillAz, fillEl);
  }

  private applyShadows(s: RenderSettings, rig: RenderRig): void {
    const type = SHADOW_TYPE[s.shadowType];

    if (this.renderer.shadowMap.type !== type) {
      this.renderer.shadowMap.type = type;
      // The shadow filter is compiled into every lit material.
      rig.scene.traverse((child) => {
        const material = (child as THREE.Mesh).material;
        if (!material) return;
        for (const m of Array.isArray(material) ? material : [material]) m.needsUpdate = true;
      });
    }
    this.renderer.shadowMap.enabled = true;

    // Every shadow-casting lamp costs each lit material a texture sampler, on
    // top of its own maps — and a shader that asks for more samplers than the
    // GPU allows fails to compile, and its mesh vanishes (a black ceiling under
    // fifteen lamps). So only the lamps nearest the middle of the room cast
    // shadows, as many as fit — and no more than four, since a point light's
    // shadow is six renders. Keeping this count steady also spares the scene
    // a recompile of every material each time the lights change.
    // The budget is for the SCENE, not each room: the samplers are counted
    // per shader, whichever rooms the lamps are in. The shown room's lamps
    // come first (nearest the middle first), then — if they are allowed
    // shadows at all — the other rooms', a round each, with lighter maps.
    const maxTextures = this.renderer.capabilities.maxTextures;
    const budget = Math.max(1, Math.min(4, maxTextures - 10));
    const lamps = this.allLamps(s, rig);
    const rankOf = (lamp: RoomLamp) => (lamp.userData.shadowRank as number | undefined) ?? 0;
    const order = [
      ...lamps.filter((l) => l.shown).sort((a, b) => rankOf(a.lamp) - rankOf(b.lamp)),
      ...(s.interiorOtherShadows ? lamps.filter((l) => !l.shown).sort((a, b) => rankOf(a.lamp) - rankOf(b.lamp)) : []),
    ];
    const casting = new Set(order.slice(0, budget).map((l) => l.lamp));
    for (const { lamp, shown } of lamps) {
      lamp.castShadow = s.shadows && casting.has(lamp);
      const resolution = shown ? s.shadowResolution : Math.min(512, s.shadowResolution);
      const key = `${s.shadowType}:${resolution}`;
      if (lamp.userData.shadowKey !== key) {
        // A shadow map is built for one filter type and one size: start again.
        lamp.shadow.map?.dispose();
        lamp.shadow.map = null as unknown as THREE.WebGLRenderTarget;
        const shadow = lamp.shadow as unknown as { mapPass: THREE.WebGLRenderTarget | null };
        shadow.mapPass?.dispose();
        shadow.mapPass = null;
        lamp.shadow.mapSize.set(resolution, resolution);
        lamp.userData.shadowKey = key;
      }
      lamp.shadow.radius = s.shadowSoftness;
      lamp.shadow.blurSamples = s.shadowSoftness > 6 ? 16 : 8;
      lamp.shadow.bias = s.shadowBias;
      lamp.shadow.normalBias = s.shadowNormalBias;
    }
    this.shadowsDirty = true;
  }

  private applyEnvironment(s: RenderSettings, rig: RenderRig): void {
    const entry = this.environment(s.environment);
    rig.scene.environment = entry?.envMap ?? null;
    this.probe.intensity = s.probeIntensity;
    const sh = this.probe.sh.coefficients;
    if (entry?.sh) {
      for (let i = 0; i < 9; i++) sh[i].copy(entry.sh[i]);
    } else {
      // No photo to take the ambient colour from: even white light.
      for (let i = 0; i < 9; i++) sh[i].set(0, 0, 0);
      sh[0].setScalar(FLAT_PROBE_SH);
    }
  }

  /** The environment for `id`, starting it loading if it is a photo not fetched yet. */
  private environment(id: EnvironmentId): EnvEntry | null {
    const ready = this.environments.get(id);
    if (ready) return ready;
    if (id === 'none') return null;

    if (id === 'studio') {
      const pmrem = new THREE.PMREMGenerator(this.renderer);
      const room = new RoomEnvironment(this.renderer);
      const entry: EnvEntry = { envMap: pmrem.fromScene(room, 0.04).texture, sh: null };
      room.dispose();
      pmrem.dispose();
      this.environments.set(id, entry);
      return entry;
    }

    const path = ENV_PATH[id];
    if (path && !this.loadingEnvironments.has(id)) {
      this.loadingEnvironments.add(id);
      loadEnvImage(this.renderer, path, (envMap, probe) => {
        this.loadingEnvironments.delete(id);
        if (this.disposed) { envMap.dispose(); return; }
        this.environments.set(id, { envMap, sh: probe.sh.coefficients.map((c) => c.clone()) });
        this.apply(this.settings);
        this.invalidate();
      });
    }
    return null;
  }

  /** The simulated grain of the lumber and of the wooden doors (shared textures: see woodTexture.ts). */
  private applyWoodRecipes(s: RenderSettings): void {
    configureWood('pine', {
      rings: s.lumberGrainLines,
      ringContrast: s.lumberGrainContrast,
      lineWidth: s.lumberLineWidth,
      waviness: s.lumberWaviness,
      fibre: s.lumberFibre,
      knots: s.lumberKnots,
      knotDarkness: s.lumberKnotDarkness,
    }, this.invalidate);
    configureWood('fine', {
      rings: s.doorGrainLines,
      ringContrast: s.doorGrainContrast,
      lineWidth: s.doorLineWidth,
      waviness: s.doorWaviness,
      fibre: s.doorFibre,
      knots: 0,
    }, this.invalidate);
  }

  /**
   * The image for a built-in floor or an upload, starting it loading if need be.
   * Null until it has loaded (and for good if it cannot be found) — callers fall
   * back to what they had; the pipeline re-applies and redraws when it arrives.
   */
  private picture(id: string): THREE.Texture | null {
    const known = this.pictures.get(id);
    if (known) return typeof known === 'string' ? null : known;
    this.pictures.set(id, 'loading');

    const use = (url: string, revoke: boolean) => {
      new THREE.TextureLoader().load(url, (texture) => {
        if (revoke) URL.revokeObjectURL(url);
        if (this.disposed) { texture.dispose(); return; }
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = 8;
        this.pictures.set(id, texture);
        this.apply(this.settings);
        this.invalidate();
      }, undefined, () => {
        if (revoke) URL.revokeObjectURL(url);
        this.pictures.set(id, 'missing');
      });
    };

    const builtin = FLOOR_TEXTURES.find((f) => f.value === id);
    if (builtin) {
      use(`${process.env.PUBLIC_URL || ''}/textures/${builtin.file}`, false);
    } else if (id.startsWith('set:')) {
      // A texture library set (the Photoreal window's preview): its colour map, from the site.
      textureSetPicture(id.slice(4)).then((pic) => {
        if (pic) use(pic.url, false);
        else this.pictures.set(id, 'missing');
      });
    } else {
      loadStoredTextureBlob(id).then((blob) => {
        if (!blob) { this.pictures.set(id, 'missing'); return; }
        use(URL.createObjectURL(blob), true);
      });
    }
    return null;
  }

  /**
   * A picture laid over something `spanU` × `spanV` feet per unit of UV, so its
   * width covers `size` feet, turned `rotation` degrees on the surface. Photos
   * are rarely seamless, so tiles are mirrored: every edge meets its own reflection.
   *
   * The UV matrix is written by hand: Three's own (repeat / rotation) turns the
   * picture in UV space, which on anything but a square surface shears it —
   * on a 20 × 12 floor a quarter turn stretched the tiles 20:12. This one goes
   * UV → feet → turned → tiles, so a tile is its real size whichever way it lies,
   * as the render lays it (render_room.py pbr_nodes: scaled, then turned).
   */
  private pictureFor(use: string, id: string, spanU: number, spanV: number, size: number, rotation: number): THREE.Texture | null {
    const image = this.picture(id);
    if (!image) return null;
    const key = `${use}:${id}`;
    let texture = this.pictureUses.get(key);
    if (!texture) {
      texture = image.clone();
      // A library set is made to tile (repeated as it is, as the render does); a photo is mirrored to hide its seams.
      const wrap = id.startsWith('set:') ? THREE.RepeatWrapping : THREE.MirroredRepeatWrapping;
      texture.wrapS = wrap;
      texture.wrapT = wrap;
      texture.matrixAutoUpdate = false;
      texture.needsUpdate = true;
      this.pictureUses.set(key, texture);
    }
    const { width, height } = image.image as { width: number; height: number };
    const tall = size * (height / Math.max(1, width)); // feet the picture covers the other way
    const a = THREE.MathUtils.degToRad(rotation);
    const c = Math.cos(a);
    const s = Math.sin(a);
    // Turned the way Three turns a texture (positive = anticlockwise seen from above), about the surface's UV origin.
    texture.matrix.set(
      (spanU * c) / size, (spanV * s) / size, 0,
      (-spanU * s) / tall, (spanV * c) / tall, 0,
      0, 0, 1,
    );
    return texture;
  }

  /**
   * The picture a texture setting names, its tile width (feet) and turn
   * (degrees): as set, or -- for a texture library set (textureSets.ts) -- the
   * set's own width times its scale, turned by its rotation. Until the set's
   * size is known the setting's own size stands in.
   */
  private texturePick(value: string, size: number, turned: boolean): { id: string; size: number; rotation: number } {
    const set = parseSetTexture(value);
    if (!set) return { id: value, size, rotation: turned ? 90 : 0 };
    const dims = textureSetSize(set.id);
    return { id: `set:${set.id}`, size: (dims ? dims.widthFt : size) * set.scale, rotation: set.rotation };
  }

  /** Wood drawn with an uploaded photo, or with the simulated grain when none is chosen (or it has not loaded). */
  private applyWoodMaterial(
    material: THREE.MeshStandardMaterial, kind: WoodKind, use: string, colour: string,
    chosen: { id: string; size: number; rotation: number; tint: number; relief: number },
  ): void {
    const photo = chosen.id ? this.pictureFor(use, chosen.id, WOOD_TILE.across, WOOD_TILE.along, chosen.size, chosen.rotation) : null;
    const map = photo ?? getWoodTexture(kind);
    if (material.map !== map) {
      material.map = map;
      material.bumpMap = map;
      material.needsUpdate = true;
    }
    material.bumpScale = chosen.relief;
    // The simulated grain is nearly white and takes the wood's colour; a photo
    // is shown as it is, tinted only as far as asked. Either way each board
    // keeps its own shade lighter or darker.
    this.tint.set(colour);
    if (photo) material.color.set(0xffffff).lerp(this.tint, chosen.tint);
    else material.color.copy(this.tint);
    material.color.offsetHSL(0, 0, (material.userData.tone as number | undefined) ?? 0);
  }

  private applyFloor(s: RenderSettings, rig: RenderRig): void {
    const floors = [
      ...(rig.floor ? [{ mesh: rig.floor, length: rig.dims.length, width: rig.dims.width, key: '' }] : []),
      ...(rig.otherFloors ?? []),
    ];
    for (const floor of floors) {
      const material = floor.mesh.material as THREE.MeshStandardMaterial;
      // Each floor has its own copy of the picture: the tiling depends on the room's size.
      const pick = this.texturePick(s.floorTexture, s.floorTextureSize, s.floorTextureRotate);
      const photo = this.pictureFor(`floor${floor.key}`, pick.id, floor.length, floor.width, pick.size, pick.rotation);
      if (photo && material.map !== photo) {
        material.map = photo;
        material.needsUpdate = true;
      }
    }
  }

  private applyMaterials(s: RenderSettings, rig: RenderRig): void {
    const glow = s.boardGlow / BASE_GLOW;
    this.applyFloor(s, rig);

    rig.roomGroup.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const material = child.material as THREE.Material;

      if (material.userData.kind === 'seam') {
        // The wall fade multiplies baseOpacity each frame (transparencyController).
        material.userData.baseOpacity = Math.min(0.98, s.seamStrength);
        const uniforms = (material as THREE.ShaderMaterial).uniforms;
        // Display values, as picked: seams go on after the tone curve.
        (uniforms.uColor.value as THREE.Color).setRGB(...hexToRgb(s.seamColor));
        uniforms.uHalfWidth.value = s.seamWidth / 24; // inches across → feet either side
        return;
      }
      if (!(material instanceof THREE.MeshStandardMaterial)) return;

      if (child === rig.floor || child.userData.planFloor) {
        if (!material.userData.baseColor) material.userData.baseColor = material.color.clone();
        material.color.copy(material.userData.baseColor as THREE.Color).multiplyScalar(s.floorBrightness);
        material.roughness = s.floorRoughness;
        material.envMapIntensity = s.floorReflection * s.envIntensity;
        return;
      }

      // Everything that glows by itself glows in proportion (boards, openings, lumber).
      if (material.userData.baseEmissive === undefined) material.userData.baseEmissive = material.emissiveIntensity;
      material.emissiveIntensity = (material.userData.baseEmissive as number) * glow;
      material.envMapIntensity = s.envIntensity;

      if (material.userData.kind === 'product') {
        material.roughness = s.boardRoughness;
        material.metalness = s.boardMetalness;
        if (material.bumpMap) material.bumpScale = s.boardBump;
      } else if (material.userData.kind === 'glass') {
        material.color.set(s.glassTint);
        material.emissive.copy(material.color);
        material.roughness = s.glassGlareSize;
        // The wall fade multiplies baseOpacity each frame (transparencyController).
        material.userData.baseOpacity = s.glassOpacity;
        const glass = material.userData.glass as Record<string, { value: unknown }>;
        // (The reflection's strength is set each frame, with its mirror picture: drawMirrors.)
        (glass.uGlassGlare as { value: number }).value = s.glassGlare;
        (glass.uGlassBase as { value: number }).value = Math.max(0.001, s.glassOpacity);
      } else if (material.userData.kind === 'lumber') {
        this.applyWoodMaterial(material, 'pine', 'lumber', s.lumberColor, {
          ...this.texturePick(s.lumberTexture, s.lumberTextureSize, s.lumberTextureRotate), tint: s.lumberTextureTint, relief: s.lumberRelief,
        });
      } else if (material.userData.kind === 'doorWood') {
        if (material.userData.tone === undefined) {
          // How much lighter or darker this part is than the door's own wood (frame boards against planks).
          material.color.getHSL(this.hsl);
          const own = this.hsl.l;
          this.tint.set(DOOR_WOOD_REFERENCE).getHSL(this.hsl);
          material.userData.tone = own - this.hsl.l;
        }
        this.applyWoodMaterial(material, 'fine', 'door', s.doorWoodColor, {
          ...this.texturePick(s.doorTexture, s.doorTextureSize, s.doorTextureRotate), tint: s.doorTextureTint, relief: s.doorRelief,
        });
        material.emissive.copy(material.color);
      }
    });
  }

  // ==========================================================================
  // Ground shadow
  // ==========================================================================

  /** A soft shadow on the backdrop around the room's footprint, like a product shot on a sweep. */
  private buildGround(rig: RenderRig): void {
    if (this.ground) {
      this.ground.geometry.dispose();
      (this.ground.material as THREE.Material).dispose();
      this.ground = null;
    }
    const { length, width } = rig.dims;
    // Every room of a floor plan stands on the one shadow.
    const rects = rig.footprints?.length ? rig.footprints : [{ x: 0, z: 0, length, width }];
    const x0 = Math.min(...rects.map((r) => r.x));
    const z0 = Math.min(...rects.map((r) => r.z));
    const x1 = Math.max(...rects.map((r) => r.x + r.length));
    const z1 = Math.max(...rects.map((r) => r.z + r.width));
    const margin = Math.max(length, width) * 0.4;
    const w = x1 - x0 + margin * 2;
    const d = z1 - z0 + margin * 2;

    // The footprint, blurred, as an alpha texture. Drawn as the SHADOW of a
    // rectangle parked off-canvas — the one blur every browser's canvas has.
    const px = 256;
    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rx = (margin / w) * px;
    const ry = (margin / d) * px;
    ctx.shadowColor = 'rgba(0, 0, 0, 1)';
    ctx.shadowBlur = Math.min(rx, ry) * 0.75;
    ctx.shadowOffsetX = px * 2;
    ctx.fillStyle = '#000';
    ctx.beginPath();
    for (const r of rects) {
      ctx.rect(((r.x - x0 + margin) / w) * px - px * 2, ((r.z - z0 + margin) / d) * px, (r.length / w) * px, (r.width / d) * px);
    }
    ctx.fill();

    this.groundTexture?.dispose();
    this.groundTexture = new THREE.CanvasTexture(canvas);
    this.groundTexture.colorSpace = THREE.SRGBColorSpace;

    const material = new THREE.MeshBasicMaterial({
      map: this.groundTexture,
      color: 0x000000,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(w, d), material);
    ground.name = 'ground-shadow';
    ground.rotation.x = -Math.PI / 2; // faces up; unseen from below
    // In the scene, not the room group: the viewer frames the camera on the
    // room group's contents, and this is much bigger than the room. The room
    // is centred on the scene's origin; the footprints are in its coordinates.
    ground.position.set(rig.roomGroup.position.x + (x0 + x1) / 2, -0.04, rig.roomGroup.position.z + (z0 + z1) / 2);
    ground.renderOrder = -1;
    ground.userData.skipAO = true;
    rig.scene.add(ground);
    this.ground = ground;

    // The blueprint ground, just under the shadow (so the shadow falls on it).
    if (this.blueprint) {
      this.blueprint.geometry.dispose();
      (this.blueprint.material as THREE.Material).dispose();
      this.blueprint = null;
    }
    const centreX = rig.roomGroup.position.x + (x0 + x1) / 2;
    const centreZ = rig.roomGroup.position.z + (z0 + z1) / 2;
    const fadeFrom = Math.hypot(x1 - x0, z1 - z0) / 2 + 2;
    // Sheet coordinates = world + offset: the room group's corner is this room's front-left corner.
    const origin = rig.sheetOrigin ?? { x: 0, z: 0 };
    const sheetSize = 2 * (fadeFrom + 220);
    const blueprint = new THREE.Mesh(
      new THREE.PlaneGeometry(sheetSize, sheetSize),
      new THREE.ShaderMaterial({
        vertexShader: BLUEPRINT_VERTEX,
        fragmentShader: BLUEPRINT_FRAGMENT,
        uniforms: {
          uSheet: { value: new THREE.Color('#153e6e') },
          uSheetOpacity: { value: 0.9 },
          uLine: { value: new THREE.Color('#eaf2ff') },
          uLineOpacity: { value: 0.35 },
          uLineWidth: { value: 1 },
          uMajorEvery: { value: 5 },
          uMajorWeight: { value: 1.8 },
          uOffset: { value: new THREE.Vector2(origin.x - rig.roomGroup.position.x, origin.z - rig.roomGroup.position.z) },
          uCentre: { value: new THREE.Vector2(centreX, centreZ) },
          uFadeFrom: { value: fadeFrom },
          uFadeTo: { value: fadeFrom + 30 },
        },
        transparent: true,
        depthWrite: false,
        // (Premultiplied output: the scene target keeps colour × coverage.)
        premultipliedAlpha: true,
        blending: THREE.CustomBlending,
        blendSrc: THREE.OneFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
        blendSrcAlpha: THREE.OneFactor,
        blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
      }),
    );
    blueprint.name = 'blueprint-ground';
    blueprint.rotation.x = -Math.PI / 2;
    blueprint.position.set(centreX, -0.06, centreZ);
    blueprint.renderOrder = -2;
    blueprint.userData.skipAO = true;
    rig.scene.add(blueprint);
    this.blueprint = blueprint;
  }

  // ==========================================================================
  // Frame
  // ==========================================================================

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    if (this.disposed) return;
    const renderer = this.renderer;
    const s = this.settings;
    renderer.getDrawingBufferSize(this.size);
    const w = Math.floor(this.size.x);
    const h = Math.floor(this.size.y);
    if (w <= 0 || h <= 0) return;
    this.ensureTargets(w, h, s);
    const sceneTarget = this.sceneTarget!;

    if (s.keyMode === 'camera') this.followCamera(camera, s);
    // The floor material loads its own default picture a moment after it is
    // built, and would swap it in over the chosen one.
    if (this.rig) this.applyFloor(s, this.rig);

    const previousBackground = scene.background;
    renderer.getClearColor(this.clearColor);
    const previousAlpha = renderer.getClearAlpha();

    // The lamps' shadow maps are drawn only when something that casts or
    // receives one has changed (a rebuild, a lamp moved, a door swinging, a
    // wall fading out) — see invalidateShadows — and then once, by whichever
    // pass comes first, not again by every pass that follows. A point lamp's
    // shadow is six scene renders: drawn every frame they were most of the
    // cost of simply looking around.
    renderer.shadowMap.autoUpdate = false;
    renderer.shadowMap.needsUpdate = this.shadowsDirty;
    this.shadowsDirty = false;

    scene.background = null;
    renderer.setClearColor(0x000000, 0);

    // 0. What each glazed wall mirrors.
    const fixtures = this.allFixtures();
    for (const fixture of fixtures) fixture.visible = s.interiorFixtures;
    this.drawMirrors(scene, camera, w, h, s);
    // A fixture hangs between the camera and a wall being edited head-on: leave it out there.
    const headOn = (camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    for (const fixture of fixtures) fixture.visible = s.interiorFixtures && !headOn;

    // 1. The scene, over nothing: the backdrop goes in behind it in the finishing pass.
    renderer.setRenderTarget(sceneTarget);
    renderer.clear();
    renderer.render(scene, camera);
    scene.background = previousBackground;

    // 2. Ambient occlusion.
    let lit = sceneTarget;
    if (s.ao && s.aoIntensity > 0 && this.aoTarget) {
      this.ensureOcclusion(scene, camera);
      this.prepareOcclusion(scene, camera, w, h, s);
      this.gtao!.render(renderer, this.aoTarget, sceneTarget, 0, false);
      lit = this.aoTarget;
    }

    // 3. Bloom — gathered, and handed to the finishing pass rather than blended in here.
    const u = this.finish.uniforms;
    if (s.bloom && s.bloomStrength > 0 && this.bloom) {
      (this.bloom.copyUniforms as { opacity: { value: number } }).opacity.value = 0;
      this.bloom.render(renderer, lit, lit, 0, false);
      u.tBloom.value = this.bloom.renderTargetsHorizontal[0].texture;
      // The pass sums five blur levels whose weights always total 3; divided
      // out, "1.0" adds a glow as bright as the area that casts it.
      u.uBloom.value = s.bloomStrength / 3;
    } else {
      u.tBloom.value = null;
      u.uBloom.value = 0;
    }

    // 4. Seams, as a mask for the finishing pass.
    const seamsDrawn = s.seamStrength > 0.005 && this.drawSeams(scene, camera);
    u.tSeams.value = seamsDrawn ? this.seamTarget!.texture : null;
    u.uSeams.value = seamsDrawn ? 1 : 0;

    // 5. Finish, to the screen — at the metered exposure (exposureMeter.ts) with the
    //    settings' exposure as an offset, or at the settings' exposure alone.
    u.tDiffuse.value = lit.texture;
    (u.uTexel.value as THREE.Vector2).set(1 / w, 1 / h);
    u.toneMappingExposure.value = s.exposure * (s.autoExposure ? Math.pow(2, this.autoEv) : 1);
    renderer.setRenderTarget(null);
    this.quad.render(renderer);

    // 6. The meter, every other frame, from the lit scene drawn above.
    if (s.autoExposure && (this.frame++ & 1) === 0) this.meter(scene, camera, lit);

    renderer.setClearColor(this.clearColor, previousAlpha);
  }

  /**
   * The mirror picture for each wall that has glass: the room drawn from the
   * camera's mirror image in that wall's plane, with the wall itself left out
   * and the walls the view has faded away put back (they are still in the room,
   * and a window would reflect them). The glass samples it at the same place on
   * screen, so what you see in a pane is exactly what a mirror there would show.
   */
  private drawMirrors(scene: THREE.Scene, camera: THREE.Camera, w: number, h: number, s: RenderSettings): void {
    const rig = this.rig;
    const drawn = new Set<WallSide>();
    for (const { face, wall, glass } of this.glazing) {
      const uniforms = (glass.material as THREE.Material).userData.glass as {
        tGlassMirror: { value: THREE.Texture | null }; uGlassMirrorMatrix: { value: THREE.Matrix4 }; uGlassReflection: { value: number };
      };
      uniforms.uGlassReflection.value = 0;
      // The head-on editing view (orthographic) gets no reflection: from square
      // in front, a window mirrors the wall facing it straight back onto itself,
      // and a window across the room shows up as a second frame behind the glass.
      const orthographic = (camera as THREE.OrthographicCamera).isOrthographicCamera === true;
      if (!rig || s.glassReflection <= 0 || !wall.visible || orthographic) continue;

      // The glass's plane, in world coordinates (it sits a little way into the wall).
      const { length, width } = rig.dims;
      const origin = rig.roomGroup.position;
      const set = 1.5 / 12;
      if (face === 'front') { PLANE_NORMAL.set(0, 0, 1); PLANE_POINT.set(0, 0, origin.z - set); }
      else if (face === 'back') { PLANE_NORMAL.set(0, 0, -1); PLANE_POINT.set(0, 0, origin.z + width + set); }
      else if (face === 'left') { PLANE_NORMAL.set(1, 0, 0); PLANE_POINT.set(origin.x - set, 0, 0); }
      else { PLANE_NORMAL.set(-1, 0, 0); PLANE_POINT.set(origin.x + length + set, 0, 0); }

      camera.updateMatrixWorld();
      CAMERA_POSITION.setFromMatrixPosition(camera.matrixWorld);
      if (VIEW.subVectors(PLANE_POINT, CAMERA_POSITION).dot(PLANE_NORMAL) > 0) continue; // seen from behind

      if (!drawn.has(face)) {
        drawn.add(face);
        const mirror = this.mirrorFor(face, w, h);
        const virtual = this.mirrorCameras.perspective;

        // The camera, reflected in the plane (as three's Reflector does it).
        VIEW.reflect(PLANE_NORMAL).negate().add(PLANE_POINT);
        ROTATION.extractRotation(camera.matrixWorld);
        LOOK_AT.set(0, 0, -1).applyMatrix4(ROTATION).add(CAMERA_POSITION);
        TARGET.subVectors(PLANE_POINT, LOOK_AT).reflect(PLANE_NORMAL).negate().add(PLANE_POINT);
        virtual.position.copy(VIEW);
        virtual.up.set(0, 1, 0).applyMatrix4(ROTATION).reflect(PLANE_NORMAL);
        virtual.lookAt(TARGET);
        virtual.updateMatrixWorld();
        virtual.projectionMatrix.copy((camera as THREE.PerspectiveCamera).projectionMatrix);
        virtual.projectionMatrixInverse.copy(virtual.projectionMatrix).invert();

        // Where a point of the world falls in the mirror picture.
        MIRROR_MATRIX.set(0.5, 0, 0, 0.5, 0, 0.5, 0, 0.5, 0, 0, 0.5, 0.5, 0, 0, 0, 1)
          .multiply(virtual.projectionMatrix).multiply(virtual.matrixWorldInverse);

        // Nothing behind the glass belongs in its reflection: make the plane the
        // near clipping plane (an oblique frustum; works for either projection).
        MIRROR_PLANE.setFromNormalAndCoplanarPoint(PLANE_NORMAL, PLANE_POINT).applyMatrix4(virtual.matrixWorldInverse);
        CLIP.set(MIRROR_PLANE.normal.x, MIRROR_PLANE.normal.y, MIRROR_PLANE.normal.z, MIRROR_PLANE.constant);
        CORNER.set(Math.sign(CLIP.x), Math.sign(CLIP.y), 1, 1).applyMatrix4(virtual.projectionMatrixInverse);
        CLIP.multiplyScalar(2 / CLIP.dot(CORNER));
        const e = virtual.projectionMatrix.elements;
        e[2] = CLIP.x - e[3];
        e[6] = CLIP.y - e[7];
        e[10] = CLIP.z - e[11];
        e[14] = CLIP.w - e[15];
        virtual.projectionMatrixInverse.copy(virtual.projectionMatrix).invert();

        // The room as it really is: this wall (and its studs) out of the way,
        // every other wall there and solid.
        this.showWholeRoom(rig, wall, face);
        this.renderer.setRenderTarget(mirror);
        this.renderer.clear();
        this.renderer.render(scene, virtual);
        this.restoreRoom();
        MIRROR_MATRICES.set(face, MIRROR_MATRIX.clone());
      }

      uniforms.tGlassMirror.value = this.mirrors.get(face)!.texture;
      uniforms.uGlassMirrorMatrix.value.copy(MIRROR_MATRICES.get(face)!);
      uniforms.uGlassReflection.value = s.glassReflection;
    }
  }

  private mirrorFor(face: WallSide, w: number, h: number): THREE.WebGLRenderTarget {
    // Half size: a reflection is seen through glass, never studied.
    const mw = Math.max(1, Math.round(w / 2));
    const mh = Math.max(1, Math.round(h / 2));
    let mirror = this.mirrors.get(face);
    if (!mirror) {
      mirror = new THREE.WebGLRenderTarget(mw, mh, { type: THREE.HalfFloatType, depthBuffer: true });
      mirror.texture.name = `designer.mirror.${face}`;
      this.mirrors.set(face, mirror);
    } else if (mirror.width !== mw || mirror.height !== mh) {
      mirror.setSize(mw, mh);
    }
    return mirror;
  }

  /** For a mirror picture: hide `wall` and its framing, and bring back whatever the view has faded out. */
  private showWholeRoom(rig: RenderRig, wall: THREE.Object3D, face: WallSide): void {
    const hide = (object: THREE.Object3D | undefined) => {
      if (!object) return;
      this.shown.push({ object, visible: object.visible });
      object.visible = false;
    };
    hide(wall);
    hide(rig.roomGroup.getObjectByName(`framing-${face}`));
    for (const child of rig.roomGroup.children) {
      if (child === wall || !child.userData.wallFace) continue;
      this.shown.push({ object: child, visible: child.visible });
      child.visible = true;
      child.traverse((part) => {
        const material = (part as THREE.Mesh).material as THREE.Material | undefined;
        if (!material || Array.isArray(material) || material.userData.kind === 'seam') return;
        const full = typeof material.userData.baseOpacity === 'number' ? material.userData.baseOpacity : 1;
        if (material.opacity === full) return;
        this.forced.push({ material, opacity: material.opacity, depthWrite: material.depthWrite });
        material.opacity = full;
        material.depthWrite = full >= 1;
      });
    }
  }

  private restoreRoom(): void {
    for (const { object, visible } of this.shown) object.visible = visible;
    for (const { material, opacity, depthWrite } of this.forced) {
      material.opacity = opacity;
      material.depthWrite = depthWrite;
    }
    this.shown.length = 0;
    this.forced.length = 0;
  }

  /**
   * Draw the seams into their own target: first the depth of everything solid
   * (so a seam behind trim, a window or another wall stays hidden), then each
   * seam over it. Returns false if there was nothing to draw.
   */
  private drawSeams(scene: THREE.Scene, camera: THREE.Camera): boolean {
    const target = this.seamTarget;
    if (!target) return false;
    const drawable = this.seams.filter((seam) => {
      if ((seam.material as THREE.Material).opacity <= 0.004) return false; // its wall is faded out
      for (let p = seam.parent; p; p = p.parent) if (!p.visible) return false;
      return true;
    });
    if (drawable.length === 0) return false;

    const renderer = this.renderer;
    const background = scene.background;
    scene.background = null;
    renderer.setRenderTarget(target);
    renderer.setClearColor(0x000000, 0);

    this.hideNonSurfaces(scene);
    scene.overrideMaterial = this.depthOnly;
    renderer.render(scene, camera); // clears, then depth only
    scene.overrideMaterial = null;
    this.restoreVisibility();
    scene.background = background;

    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    for (const seam of drawable) {
      seam.visible = true;
      renderer.render(seam, camera);
      seam.visible = false;
    }
    renderer.autoClear = autoClear;
    return true;
  }

  /**
   * Hide everything that is not a solid surface: lines and sprites (the
   * dimension labels), and whatever is marked skipAO (faded walls, the ground
   * shadow). For passes that measure the scene's depth.
   */
  private hideNonSurfaces(root: THREE.Object3D): void {
    root.traverse((object) => {
      this.visibility.set(object, object.visible);
      const o = object as THREE.Object3D & { isPoints?: boolean; isLine?: boolean; isSprite?: boolean };
      if (o.isPoints || o.isLine || o.isSprite || o.userData.skipAO) object.visible = false;
    });
  }

  private restoreVisibility(): void {
    for (const [object, visible] of this.visibility) object.visible = visible;
    this.visibility.clear();
  }

  /** The key light rides with the camera: off to one side and above the line of sight. */
  private followCamera(camera: THREE.Camera, s: RenderSettings): void {
    camera.getWorldDirection(this.viewDir).negate(); // from the scene toward the camera
    const azimuth = THREE.MathUtils.radToDeg(Math.atan2(this.viewDir.x, this.viewDir.z));
    const elevation = THREE.MathUtils.radToDeg(Math.asin(Math.max(-1, Math.min(1, this.viewDir.y))));
    this.aimKeyLights(azimuth + s.keyAzimuth, elevation + s.keyElevation, azimuth - s.keyAzimuth * 1.4, elevation + 5);
  }

  private ensureTargets(w: number, h: number, s: RenderSettings): void {
    let samples = s.antialias;
    while (samples > 0 && w * h * samples > MAX_SAMPLE_PIXELS) samples = samples > 2 ? samples / 2 : 0;
    const key = `${w}x${h}:${samples}`;
    if (key !== this.targetKey) {
      this.sceneTarget?.dispose();
      this.aoTarget?.dispose();
      this.sceneTarget = new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, samples, depthBuffer: true });
      this.sceneTarget.texture.name = 'designer.scene';
      this.aoTarget = new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, depthBuffer: false });
      this.aoTarget.texture.name = 'designer.occluded';
      this.seamTarget?.dispose();
      this.seamTarget = new THREE.WebGLRenderTarget(w, h, { depthBuffer: true });
      this.seamTarget.texture.name = 'designer.seams';
      this.targetKey = key;
      this.aoKey = '';
      this.bloom?.setSize(w, h);
    }
    if (s.bloom && !this.bloom) {
      this.bloom = new UnrealBloomPass(new THREE.Vector2(w, h), 1, s.bloomRadius, s.bloomThreshold);
      (this.bloom.highPassUniforms as { smoothWidth: { value: number } }).smoothWidth.value = s.bloomKnee;
    }
  }

  private prepareOcclusion(scene: THREE.Scene, camera: THREE.Camera, w: number, h: number, s: RenderSettings): void {
    const gtao = this.gtao!;
    gtao.scene = scene;
    gtao.camera = camera;

    const scale = s.aoHalfRes ? 0.5 : 1;
    const key = `${w}x${h}:${scale}`;
    if (key !== this.aoKey) {
      gtao.setSize(Math.max(1, Math.round(w * scale)), Math.max(1, Math.round(h * scale)));
      this.aoKey = key;
    }

    // The pass is compiled for one kind of camera; the designer swaps between
    // perspective (orbit) and orthographic (a locked wall).
    const perspective = (camera as THREE.PerspectiveCamera).isPerspectiveCamera ? 1 : 0;
    const depthMaterial = (gtao as unknown as { depthRenderMaterial: THREE.ShaderMaterial }).depthRenderMaterial;
    for (const material of [gtao.gtaoMaterial, depthMaterial]) {
      if (material.defines.PERSPECTIVE_CAMERA !== perspective) {
        material.defines.PERSPECTIVE_CAMERA = perspective;
        material.needsUpdate = true;
      }
    }
  }

  /** Created on first use: the pass needs a scene and camera to exist. */
  private ensureOcclusion(scene: THREE.Scene, camera: THREE.Camera): void {
    if (this.gtao || this.disposed) return;
    const gtao = new GTAOPass(scene, camera, 4, 4);
    // Thin, see-through and "not a surface" things stay out of the depth the
    // occlusion is measured against: lines and sprites (the dimension labels),
    // and anything marked skipAO (seam tints, faded walls, the ground shadow).
    gtao.overrideVisibility = () => this.hideNonSurfaces(gtao.scene);
    gtao.restoreVisibility = () => this.restoreVisibility();
    // Surfaces are drawn two-sided; measure them the same way.
    gtao.normalMaterial.side = THREE.DoubleSide;
    this.gtao = gtao;
    this.applyOcclusion(this.settings);
  }

  // ==========================================================================

  dispose(): void {
    this.disposed = true;
    this.sceneTarget?.dispose();
    this.aoTarget?.dispose();
    this.seamTarget?.dispose();
    this.depthOnly.dispose();
    this.seams = [];
    this.glazing = [];
    for (const mirror of this.mirrors.values()) mirror.dispose();
    this.mirrors.clear();
    this.renderer.shadowMap.autoUpdate = true;
    this.gtao?.dispose();
    this.bloom?.dispose();
    this.meterTarget.dispose();
    this.meterMaterial.dispose();
    this.maskTarget.dispose();
    this.maskMaterial.dispose();
    this.meterQuad.dispose();
    this.finish.dispose();
    this.groundTexture?.dispose();
    if (this.ground) {
      this.ground.geometry.dispose();
      (this.ground.material as THREE.Material).dispose();
    }
    if (this.blueprint) {
      this.blueprint.geometry.dispose();
      (this.blueprint.material as THREE.Material).dispose();
    }
    for (const texture of this.pictureUses.values()) texture.dispose();
    for (const picture of this.pictures.values()) if (typeof picture !== 'string') picture.dispose();
    this.pictureUses.clear();
    this.pictures.clear();
    for (const entry of this.environments.values()) entry.envMap?.dispose();
    this.environments.clear();
    this.lampSets.forEach((set) => {
      set.created.forEach((lamp) => lamp.dispose());
      set.fixtures.forEach(disposeFixture);
    });
    this.lampSets.clear();
    this.rig = null;
  }
}
